import { useMemo, useState } from "react";
import { createFileRoute, useNavigate, useParams } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { type SortingState } from "@tanstack/react-table";
import { useCatalogManufacturer } from "@/hooks/use-catalog-manufacturers";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import {
  useCatalogAssemblies,
  type CatalogAssembliesParams,
  type CatalogAssemblySortField,
} from "@/hooks/use-catalog-assemblies";
import {
  Badge,
  Breadcrumbs,
  Card,
  CardContent,
  CardHeader,
  DataTable,
  EmptyState,
  PageHeader,
  Pagination,
  Skeleton,
  SkeletonTable,
  catalogStatusVariant,
} from "@/components/ui";
import { useAssemblyColumns } from "@/components/catalog/assembly-columns";

export const Route = createFileRoute("/_authenticated/catalog/manufacturers/$id")({
  component: CatalogManufacturerDetailPage,
});

const PAGE_SIZE = 25;

// Mirrors the assemblies tab of the consolidated catalog page. The factory
// id `lead_free` maps to API enum `is_lead_free`; everything else round-trips.
const COLUMN_TO_SORT: Record<string, CatalogAssemblySortField> = {
  model: "model",
  manufacturer_name: "manufacturer_name",
  assembly_type: "assembly_type",
  size: "size",
  orientation: "orientation",
  lead_free: "is_lead_free",
  last_approval: "last_approval",
  status: "status",
};

function CatalogManufacturerDetailPage() {
  const { t } = useTranslation(["catalog", "common", "nav"]);
  const { id } = useParams({ from: "/_authenticated/catalog/manufacturers/$id" });
  const navigate = useNavigate();

  const manufacturerQuery = useCatalogManufacturer(id);
  useRecentPageTitle(manufacturerQuery.data?.name ?? undefined);

  const [page, setPage] = useState(0);
  const [sorting, setSorting] = useState<SortingState>([{ id: "model", desc: false }]);

  const assemblyParams: CatalogAssembliesParams = useMemo(() => {
    const first = sorting[0];
    const sortField = first ? COLUMN_TO_SORT[first.id] : undefined;
    return {
      manufacturer_id: id,
      sort: sortField,
      order: first ? (first.desc ? "desc" : "asc") : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    };
  }, [id, page, sorting]);

  const assembliesQuery = useCatalogAssemblies(assemblyParams);
  const assemblies = assembliesQuery.data?.items ?? [];
  const totalAssemblies = assembliesQuery.data?.total ?? 0;
  const totalPages = Math.ceil(totalAssemblies / PAGE_SIZE);

  // On the manufacturer detail page every assembly row shares the same
  // `manufacturer_name` (the page is filtered by `manufacturer_id`), so we
  // omit that column to reclaim horizontal space. Status varies per
  // assembly within a single manufacturer (USC FCCCHR can suspend
  // individual models while the manufacturer is otherwise approved), so
  // it stays.
  const columns = useAssemblyColumns({ excludeColumns: ["manufacturer_name"] });

  function handleSortingChange(next: SortingState) {
    setSorting(next);
    setPage(0);
  }

  if (manufacturerQuery.isLoading) {
    return (
      <div className="space-y-4 p-8">
        <Skeleton variant="line" />
        <Skeleton variant="card" />
      </div>
    );
  }

  if (manufacturerQuery.isError || !manufacturerQuery.data) {
    return (
      <div className="p-8">
        <Breadcrumbs
          items={[
            { label: t("catalog.group", { ns: "nav" }) },
            {
              label: t("page.manufacturersTitle"),
              to: "/catalog/assemblies",
              search: { tab: "manufacturers" },
            },
            { label: t("page.manufacturerDetailTitle") },
          ]}
        />
        <PageHeader title={t("page.manufacturerDetailTitle")} />
        <div className="mt-6">
          <EmptyState
            title={t("page.manufacturerDetailTitle")}
            description={t("errors.loadFailed")}
          />
        </div>
      </div>
    );
  }

  const manufacturer = manufacturerQuery.data;
  const addressLines = [
    manufacturer.address_line1,
    manufacturer.address_line2,
    [manufacturer.city, manufacturer.state, manufacturer.postal_code].filter(Boolean).join(", "),
  ].filter((line) => line && line.length > 0);

  return (
    <div className="p-8">
      <Breadcrumbs
        items={[
          { label: t("catalog.group", { ns: "nav" }) },
          {
            label: t("page.manufacturersTitle"),
            to: "/catalog/assemblies",
            search: { tab: "manufacturers" },
          },
          { label: manufacturer.name },
        ]}
      />
      <PageHeader
        title={manufacturer.name}
        actions={
          <Badge variant={catalogStatusVariant(manufacturer.status)}>
            {t(`status.${manufacturer.status}`)}
          </Badge>
        }
      />

      <Card className="mt-6">
        <CardHeader>
          <h3 className="text-base font-semibold text-on-surface">
            {t("manufacturer.contactSection")}
          </h3>
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
            <div>
              <dt className="text-sm font-medium text-muted">{t("manufacturer.fields.address")}</dt>
              <dd className="mt-1 text-sm text-on-surface">
                {addressLines.length > 0
                  ? addressLines.map((line, i) => <div key={i}>{line}</div>)
                  : "—"}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted">{t("manufacturer.fields.phone")}</dt>
              <dd className="mt-1 text-sm text-on-surface">
                {manufacturer.phone ? (
                  <a href={`tel:${manufacturer.phone}`} className="text-primary hover:underline">
                    {manufacturer.phone}
                  </a>
                ) : (
                  "—"
                )}
              </dd>
            </div>
            <div>
              <dt className="text-sm font-medium text-muted">{t("manufacturer.fields.website")}</dt>
              <dd className="mt-1 text-sm text-on-surface">
                {manufacturer.website_url ? (
                  <a
                    href={manufacturer.website_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    {manufacturer.website_url.replace(/^https?:\/\//, "")}
                  </a>
                ) : (
                  "—"
                )}
              </dd>
            </div>
          </dl>
        </CardContent>
      </Card>

      <div className="mt-8">
        <h2 className="text-lg font-semibold text-on-surface">{t("page.assembliesTitle")}</h2>

        {assembliesQuery.isLoading ? (
          <div className="mt-3">
            <SkeletonTable columns={7} rows={5} />
          </div>
        ) : assemblies.length === 0 ? (
          <div className="mt-3">
            <EmptyState title={t("page.assembliesTitle")} description={t("empty.assemblies")} />
          </div>
        ) : (
          <>
            <div className="mt-3">
              <DataTable
                columns={columns}
                data={assemblies}
                sorting={sorting}
                onSortingChange={handleSortingChange}
                onRowClick={(row) =>
                  void navigate({
                    to: "/catalog/assemblies/$id",
                    params: { id: row.id },
                  })
                }
              />
            </div>
            <Pagination
              currentPage={page + 1}
              totalPages={totalPages}
              totalItems={totalAssemblies}
              pageSize={PAGE_SIZE}
              onPageChange={(p) => setPage(p - 1)}
            />
          </>
        )}
      </div>
    </div>
  );
}
